import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, ChevronLeft, RefreshCw } from 'lucide-react';
import { AdminApi, ApiError } from '../../services/api';
import type { Ticket } from '../../types';
import { Button } from '../../shared/ui';

/** Ce que la page transmet à la fiche : le dossier chargé et le moyen de le tenir à jour. */
export interface ProprietesFiche {
  ticket: Ticket;
  onUpdate: (t: Ticket) => void;
  /** Le dossier transmis est complet (historique compris) : la fiche n'a pas à le relire à l'ouverture. */
  dejaCharge?: boolean;
}

/** Repère posé sur la page d'un dossier (raccourcis clavier). */
export const ATTRIBUT_PAGE_DOSSIER = 'data-page-dossier';

/**
 * Une touche frappée vise-t-elle la page du dossier ? Non si une fenêtre est
 * ouverte, ni si le focus est hors de la page (en-tête, notifications,
 * assistant). Oui quand le focus n'est nulle part en particulier (corps de page).
 */
export function toucheVisePageDossier(cible: EventTarget | null, doc: Document = document): boolean {
  if (doc.querySelector('[role="dialog"]')) return false;
  if (typeof Element === 'undefined' || !(cible instanceof Element)) return true;
  if (cible === doc.body || cible === doc.documentElement) return true;
  return cible.closest(`[${ATTRIBUT_PAGE_DOSSIER}]`) !== null;
}

export type MotifErreurDossier = 'introuvable' | 'refuse' | 'autre';

/** Traduit l'échec du chargement d'un dossier en motif affichable. */
export function motifErreurDossier(error: unknown): { motif: MotifErreurDossier; message: string } {
  if (error instanceof ApiError && error.statusCode === 404) {
    return { motif: 'introuvable', message: "Ce dossier n'existe pas ou a été supprimé." };
  }
  if (error instanceof ApiError && error.statusCode === 403) {
    return { motif: 'refuse', message: "Vous n'avez pas accès à ce dossier." };
  }
  return { motif: 'autre', message: error instanceof Error && error.message ? error.message : "Le dossier n'a pas pu être chargé." };
}

type Etat =
  | { etat: 'chargement' }
  | { etat: 'pret'; dossier: Ticket }
  | { etat: 'erreur'; motif: MotifErreurDossier; message: string };

const TITRES_ERREUR: Record<MotifErreurDossier, string> = {
  introuvable: 'Dossier introuvable',
  refuse: 'Accès refusé',
  autre: 'Chargement impossible',
};

/**
 * Fiche d'un dossier sur sa propre page (/dossiers/:id), dans la mise en page
 * de l'admin. Le dossier est lu par son identifiant : un rechargement ou un
 * lien partagé rouvrent la même fiche. La fiche elle-même (onglets,
 * conversation, temps réel…) est fournie par l'écran principal.
 */
export const PageDossier: React.FC<{
  Fiche: React.ComponentType<ProprietesFiche>;
  surRetour: () => void;
}> = ({ Fiche, surRetour }) => {
  const { id = '' } = useParams<{ id: string }>();
  // Une page neuve par dossier : rien ne passe d'une fiche à l'autre.
  return <ContenuPageDossier key={id} id={id} Fiche={Fiche} surRetour={surRetour} />;
};

const ContenuPageDossier: React.FC<{
  id: string;
  Fiche: React.ComponentType<ProprietesFiche>;
  surRetour: () => void;
}> = ({ id, Fiche, surRetour }) => {
  const [etat, setEtat] = useState<Etat>({ etat: 'chargement' });
  const [essai, setEssai] = useState(0);
  const haut = useRef<HTMLDivElement>(null);

  // La page s'ouvre en haut, même si l'écran précédent était défilé.
  useEffect(() => {
    const zone = haut.current?.closest('main');
    if (zone) zone.scrollTop = 0;
  }, []);

  useEffect(() => {
    let vivant = true;
    setEtat({ etat: 'chargement' });
    AdminApi.getTicketById(id)
      .then(dossier => { if (vivant) setEtat({ etat: 'pret', dossier }); })
      .catch((error: unknown) => {
        if (!vivant) return;
        console.error('Erreur chargement dossier:', error);
        setEtat({ etat: 'erreur', ...motifErreurDossier(error) });
      });
    return () => { vivant = false; };
  }, [id, essai]);

  // Mise à jour venue de la fiche (enregistrement, relecture, temps réel).
  const surMiseAJour = (t: Ticket) => {
    setEtat(e => (e.etat === 'pret' && e.dossier.id === t.id ? { etat: 'pret', dossier: t } : e));
  };

  return (
    // Sur téléphone (< 640 px), retraits réduits : la fiche garde sa largeur
    // pour le contenu. La fiche (FicheDossier) suit le retrait intérieur.
    <div ref={haut} className="p-4 max-sm:p-2" data-page-dossier="">
      <div className="max-w-6xl mx-auto bg-white rounded-card border border-trait">
        <div className="flex items-center justify-between px-5 py-4 max-sm:px-3 max-sm:py-2 border-b border-trait">
          <Button variant="ghost" size="sm" onClick={surRetour}>
            <ChevronLeft className="w-4 h-4 mr-1" />Retour aux dossiers
          </Button>
        </div>
        <div className="p-5 max-sm:p-3">
          {etat.etat === 'chargement' && (
            <div className="py-12 text-center text-graphite">
              <RefreshCw className="w-6 h-6 mx-auto mb-3 animate-spin" />
              <p className="text-sm">Chargement du dossier…</p>
            </div>
          )}
          {etat.etat === 'erreur' && (
            <div role="alert" className="py-12 text-center">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-600" />
              <p className="text-base font-semibold text-encre">{TITRES_ERREUR[etat.motif]}</p>
              <p className="text-sm text-graphite mt-1">{etat.message}</p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Button variant="primary" size="sm" onClick={surRetour}>Retour aux dossiers</Button>
                {etat.motif === 'autre' && (
                  <Button variant="secondary" size="sm" onClick={() => setEssai(n => n + 1)}>Réessayer</Button>
                )}
              </div>
            </div>
          )}
          {etat.etat === 'pret' && <Fiche ticket={etat.dossier} onUpdate={surMiseAJour} dejaCharge />}
        </div>
      </div>
    </div>
  );
};

export default PageDossier;
